<template>
  <div
    v-if="is_initialized"
    class="flex flex-col gap-6"
  >
    <slot
      :is-visible="is_edit"
      name="lead-form-header"
      :reset="setInitial"
      :submit="validate"
    />

    <section>
      <TitleForm class="mb-5">
        {{ $t('contact.labels.general') }}
      </TitleForm>
      <b-field grouped>
        <SelectContactTitle
          :disabled="disabled"
          :is-expanded="false"
          :title_prop="lead.attributes.title"
          @change="is_edit = true"
          @input="(title) => (lead.attributes.title = title)"
        />
        <b-field
          expanded
          :label="`${$t('lead.attributes.first_name')} *`"
          labelFor="first-name"
        >
          <div class="field">
            <input
              id="first-name"
              v-model.trim="v$.lead.attributes.first_name.$model"
              class="input"
              :class="{ 'is-danger': v$.lead.attributes.first_name.$error }"
              data-test="lead-first-name"
              :disabled="disabled"
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.lead.attributes.first_name.$error">
              <p
                v-if="v$.lead.attributes.first_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('lead.validation.needFirstName') }}
              </p>
            </div>
          </div>
        </b-field>

        <b-field
          expanded
          :label="`${$t('lead.attributes.last_name')} *`"
          labelFor="last-name"
        >
          <div class="field">
            <input
              id="last-name"
              v-model.trim="v$.lead.attributes.last_name.$model"
              class="input"
              :class="{ 'is-danger': v$.lead.attributes.last_name.$error }"
              data-test="lead-last-name"
              :disabled="disabled"
              required
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.lead.attributes.last_name.$error">
              <p
                v-if="v$.lead.attributes.last_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('lead.validation.needLastName') }}
              </p>
            </div>
          </div>
        </b-field>
      </b-field>

      <BField grouped>
        <b-field
          expanded
          :label="`${$t('lead.attributes.email_address')}`"
          labelFor="email-address"
        >
          <div v-if="props.action === 'create'">
            <input
              id="email-address"
              class="input grow"
              :class="{ 'is-danger': v$.lead.attributes.email_address.$error }"
              data-test="lead-email-address"
              :disabled="disabled"
              required
              @input="debouncedEmailHandler"
            />
            <div
              v-if="v$.lead.attributes.email_address.$error"
              data-test="email-validation-errors"
            >
              <p
                v-if="v$.lead.attributes.email_address.email.$invalid"
                class="help is-danger"
              >
                {{ $t('profile.validation.mustBeEmailAddress') }}
              </p>
              <p
                v-if="v$.lead.attributes.email_address.emailAddressUniq.$invalid"
                class="help is-danger"
              >
                {{ $t('profile.validation.emailTaken') }}
              </p>
              <p
                v-if="v$.lead.attributes.email_address.emailAddressDemo.$invalid"
                class="help is-danger"
              >
                {{ $t('validation.emailAddressDemo', { email_address_suffix }) }}
              </p>
            </div>
          </div>
          <div
            v-else
            class="field is-inline-flex items-center"
          >
            <span>
              <span
                v-if="is_userless"
                :class="{ 'text-gray-500 italic': lead.attributes.email_address === null }"
              >
                {{ lead.attributes.email_address || $t('profile.labels.noEmailAddressAdded') }}
              </span>
              <a
                v-else-if="lead.attributes.email_address === null"
                class="text-gray-500 italic"
                @click="openUpdateEmailAddressModal"
              >
                {{ $t('profile.labels.noEmailAddressAdded') }} <FontAwesomeIcon :icon="['far', 'pen']" />
              </a>
              <a
                v-else-if="lead.attributes.profiles_count <= 1"
                @click="openUpdateEmailAddressModal"
              >
                {{ lead.attributes.email_address }} <FontAwesomeIcon :icon="['far', 'pen']" />
              </a>
              <a
                v-else
                v-tooltip="$t('profile.labels.multipleProfiles')"
              >
                {{ lead.attributes.email_address }} <FontAwesomeIcon icon="circle-info" />
              </a>
            </span>
          </div>
        </b-field>
        <EmailAddressStatusIndicator
          :profile="lead"
          :show_description="true"
        />
      </BField>

      <b-field grouped>
        <b-field
          expanded
          :label="$t('lead.attributes.phone_mobile')"
          labelFor="phone_mobile"
        >
          <input
            id="phone_mobile"
            v-model.trim="lead.attributes.phone_mobile"
            class="input"
            :disabled="disabled"
            @keyup="is_edit = true"
          />
        </b-field>

        <b-field
          expanded
          :label="$t('lead.attributes.phone_landline')"
          labelFor="phone_landline"
        >
          <input
            id="phone_landline"
            v-model.trim="lead.attributes.phone_landline"
            class="input"
            :disabled="disabled"
            @keyup="is_edit = true"
          />
        </b-field>
      </b-field>

      <BField :label="$t('lead.attributes.language')">
        <SelectLanguage
          :disabled="disabled"
          :language_prop="lead.attributes.language"
          @change="is_edit = true"
          @input="(language) => (lead.attributes.language = language)"
        />
      </BField>

      <b-field :label="$t('lead.attributes.category')">
        <b-radio
          v-for="category in Lead.CATEGORIES"
          :key="category"
          v-model="lead.attributes.category"
          name="name"
          :native-value="category"
          @change="is_edit = true"
        >
          {{ $t(`lead.attributes.categories.${category}`) }}
        </b-radio>
      </b-field>
    </section>

    <section>
      <TitleForm class="mb-4">
        {{ $t('contact.labels.address') }}
      </TitleForm>
      <AddressForm
        v-model:address_city="lead.attributes.address_city"
        v-model:address_country="lead.attributes.address_country"
        v-model:address_number="lead.attributes.address_number"
        v-model:address_street="lead.attributes.address_street"
        v-model:address_zip="lead.attributes.address_zip"
        class="field"
        :disabled="disabled"
        @edit="is_edit = true"
      />
    </section>

    <section>
      <TitleForm class="mb-4">
        {{ $t('lead.attributes.internal_note') }}
      </TitleForm>
      <EditorZiggu
        v-model:content_html="lead.attributes.internal_note_html"
        :content_key="lead.id"
        data-test="lead-internal-note"
        :is_textarea="true"
        @keyup="is_edit = true"
      />
    </section>

    <footer>
      <slot
        :is-visible="is_edit"
        name="lead-form-footer"
        :reset="setInitial"
        :submit="validate"
      />
    </footer>

    <PrimeDialog
      :visible="cannot_update_field_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: lead.attributes.name })"
      @update:visible="cannot_update_field_modal = false"
    >
      <p>{{ $t('profile.labels.maintainerCannotUpdateEmailAddress') }}</p>
      <template #footer>
        <ButtonPrimary @onClick="cannot_update_field_modal = false">
          {{ $t('modal.labels.gotItMessage') }}
        </ButtonPrimary>
      </template>
    </PrimeDialog>

    <PrimeDialog
      :visible="show_update_email_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: lead.attributes.name })"
      @update:visible="show_update_email_modal = false"
    >
      <UpdateEmailAddressModal
        ref="update_email_modal_ref"
        :profile="lead"
        :email_address_old="lead.attributes.email_address"
        @close="show_update_email_modal = false"
        @updateEmailAddress="updateEmailAddress"
      />
      <template #footer>
        <div class="flex justify-end gap-2">
          <ButtonTertiary @onClick="show_update_email_modal = false">
            {{ $t('modal.actions.cancel') }}
          </ButtonTertiary>
          <ButtonPrimary @onClick="update_email_modal_ref.submit()">
            {{ $t('profile.actions.changeEmail') }}
          </ButtonPrimary>
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import EmailAddressStatusIndicator from '@/app/_employees/components/users/EmailAddressStatusIndicator.vue';
import AddressForm from '@/app/shared_components/geo/AddressForm.vue';
import SelectLanguage from '@/app/shared_components/selects/SelectLanguage.vue';
import EditorZiggu from '@/app/shared_components/EditorZiggu.vue';
import UpdateEmailAddressModal from '@/app/_employees/components/contacts/TheUpdateEmailAddressModal.vue';
import SelectContactTitle from '@/app/shared_components/selects/SelectContactTitle.vue';
import { Employee, Lead } from '@/app/data/model_constants';
import { email } from '@/app/util/validators';
import useContactEmail from '@/app/util/use_contact_email';
import useUserlessProfile from '@/composables/useUserlessProfile';
import { EDITOR_CONTENT_HTML_EMPTY } from '@/app/util/editor/editor';

const { withAsync } = helpers;

const props = defineProps({
  lead_prop: { type: Object, default: null },
  disabled: { type: Boolean },
  action: {
    type: String,
    validator: (val) => ['create', 'edit'].includes(val),
    required: true,
  },
});

const emit = defineEmits(['setLead']);

const store = useStore();
const { email_address_suffix, emailAddressDemo, isEmailUnique, createDebouncedEmailHandler } = useContactEmail();

const lead = ref(null);
const { is_userless } = useUserlessProfile(lead);
const is_edit = ref(false);
const is_initialized = ref(false);
const cannot_update_field_modal = ref(false);
const show_update_email_modal = ref(false);
const update_email_modal_ref = ref(null);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);

const rules = computed(() => ({
  lead: {
    attributes: {
      first_name: { required },
      last_name: { required },
      email_address: {
        email,
        emailAddressUniq: withAsync((email_address) => {
          if (props.action === 'edit' || email_address === '' || _.isNil(email_address)) return Promise.resolve(true);
          return isEmailUnique(email_address.trim());
        }),
        emailAddressDemo,
      },
    },
  },
}));

const v$ = useVuelidate(rules, { lead });

function setInitial() {
  is_initialized.value = false;
  if (_.get(props.lead_prop, 'id')) {
    lead.value = _.cloneDeep(props.lead_prop);
  } else {
    lead.value = {
      attributes: {
        address: '',
        birth_date: null,
        email_address: '',
        first_name: '',
        iban: '',
        internal_note_html: EDITOR_CONTENT_HTML_EMPTY,
        last_name: '',
        phone_mobile: '',
        phone_landline: '',
        language: current_client.value.attributes.language,
      },
    };
  }
  is_edit.value = false;
  is_initialized.value = true;
}

watch(
  () => props.lead_prop,
  (new_val, old_val) => {
    if (new_val && old_val && new_val.id !== old_val.id) setInitial();
  },
);
setInitial();

function openUpdateEmailAddressModal() {
  if (current_profile.value.attributes.role !== Employee.ROLE_OWNER) {
    cannot_update_field_modal.value = true;
    return;
  }
  show_update_email_modal.value = true;
}

function updateEmailAddress(email_address_new) {
  lead.value.attributes.email_address = email_address_new;
  emit('setLead', lead.value);
}

const debouncedEmailHandler = createDebouncedEmailHandler(() => v$.value.lead.attributes, lead);

function validate() {
  v$.value.$reset();
  v$.value.$touch();
  if (v$.value.lead.$invalid) return;
  emit('setLead', lead.value);
}

defineExpose({ validate });
</script>
